import { Injectable, signal, computed } from '@angular/core';
import { normalizeMerchantAccounts } from '@shared/utils/merchant-account.util';
import type { MerchantAccount, RawMerchantAccount } from '../models';

/**
 * AccountStore — the single owner of the merchant account list and the active selection.
 *
 * Accounts are keyed on `accountId` (the `MerchantAccountUser` row id), never on the raw
 * `_id` the success payload sends — that field is the *merchant* id and keying on it made
 * every `account_id` request field carry the wrong value.
 *
 * Security:
 * - Account data is held in memory only.
 * - Only the selected `accountId` is persisted (non-sensitive).
 * - Everything is cleared on logout.
 */
@Injectable({
  providedIn: 'root',
})
export class AccountStore {
  // Private signals for internal state
  private readonly accountsSignal = signal<MerchantAccount[]>([]);
  private readonly activeAccountIdSignal = signal<string | null>(null);
  private readonly switchingSignal = signal<boolean>(false);

  // Public readonly signals
  readonly accounts = this.accountsSignal.asReadonly();
  readonly activeAccountId = this.activeAccountIdSignal.asReadonly();
  readonly switching = this.switchingSignal.asReadonly();

  // Computed signals for UI
  readonly activeAccount = computed(() => {
    const accountId = this.activeAccountId();
    const accounts = this.accounts();
    return accounts.find((acc) => acc.accountId === accountId) ?? null;
  });

  readonly hasMultipleAccounts = computed(() => this.accounts().length > 1);

  readonly accountCount = computed(() => this.accounts().length);

  readonly defaultAccount = computed(() => {
    const accounts = this.accounts();
    return accounts.find((acc) => acc.isDefaultMerchant) ?? accounts[0] ?? null;
  });

  /**
   * Replace the account list.
   *
   * Normalized on the way in — this is the only writer of `accountsSignal`, so it is where
   * the `MerchantAccount` contract is enforced against raw API / localStorage data.
   */
  setAccounts(accounts: readonly RawMerchantAccount[] | null | undefined): void {
    this.accountsSignal.set(normalizeMerchantAccounts(accounts));
  }

  /**
   * Set the active account by `accountId`.
   *
   * Ignores ids that are not in the current list. That is the graceful path for a stale
   * `gozem_selected_account_id` in localStorage: before this change the persisted value was
   * a *merchant* id, so returning users carry one that matches nothing. Callers fall back
   * to the default account rather than pinning to an account that does not exist.
   */
  setActiveAccount(accountId: string): void {
    const accountExists = this.accounts().some((acc) => acc.accountId === accountId);

    if (accountExists) {
      this.activeAccountIdSignal.set(accountId);
    } else {
      console.error(`Account ${accountId} not found in store`);
    }
  }

  /**
   * Set account switching state
   */
  setSwitching(switching: boolean): void {
    this.switchingSignal.set(switching);
  }

  /**
   * Clear all account data (called on logout)
   */
  clearAccounts(): void {
    this.accountsSignal.set([]);
    this.activeAccountIdSignal.set(null);
    this.switchingSignal.set(false);
  }

  /**
   * Get account by `accountId`
   */
  getAccountById(accountId: string): MerchantAccount | null {
    return this.accounts().find((acc) => acc.accountId === accountId) ?? null;
  }

  /**
   * Check if an account exists in the store
   */
  hasAccount(accountId: string): boolean {
    return this.accounts().some((acc) => acc.accountId === accountId);
  }

  /**
   * Check if accounts are loaded
   */
  hasAccounts(): boolean {
    return this.accounts().length > 0;
  }

  /**
   * The `account_id` every authenticated request should carry.
   *
   * Falls back to the default account so a request fired before the store is fully
   * initialised still resolves something sensible. Returns `''` rather than `null` because
   * every call site feeds it straight into a request body.
   *
   * Prefer this over reading `accounts[0]` directly: that pattern pins the whole app to the
   * first merchant and silently ignores which one the user actually selected.
   */
  resolveAccountId(): string {
    return (this.activeAccount() ?? this.defaultAccount())?.accountId ?? '';
  }
}
